import React from 'react';
import { Row } from 'mage-components';
import { HorizontalWrapper } from 'moet-core';
import { SpacerVertical, SpacerHorizontal } from '@klarna/bubble-ui';
import {
  ContainerTemplate,
  SummarySection,
  CallToActionButton,
  SecondaryButton,
} from '@/components';
import { Payload } from '@mjolnir/mf-fields-ts/src/Payload';
import { BpcPayload } from '@/bpc/types/BpcPayload';
import { Onboarding } from '@/bpc/components/BaseComponents/Onboarding';
import {
  WithTermsAndConditions,
  WithTermsAndConditionsChildProps,
} from '@/bpc/components/HOCs/WithTermsAndConditions';

import { OnboardingGroup } from '@/bpc/components/OnboardingGroupComponent/OnboardingGroup';
import { OnboardingSummaryFieldComponent } from './OnboardingSummaryFieldComponent';

const tPrefixButtons = 'products-fe.general.buttons';

interface OnboardingSummaryComponentProps {
  payload: Payload;
  group: OnboardingGroup;
  error?: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  onboarding: Onboarding;
  onSummaryPageBack: () => void;
  onSubmit: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: TranslatorFunction;
  bpcPayloadWithTerms: BpcPayload;
}

interface OnboardingSummaryComponentState {
  isLoading: boolean;
  ctaClicked: boolean;
  errorOnSubmit: string | null;
}

type ComponentProps = OnboardingSummaryComponentProps & WithTermsAndConditionsChildProps;
class _OnboardingSummaryComponent extends React.Component<
  ComponentProps,
  OnboardingSummaryComponentState
> {
  constructor(props: ComponentProps) {
    super(props);

    this.state = { ctaClicked: false, errorOnSubmit: null, isLoading: false };

    this.onSubmit = this.onSubmit.bind(this);
  }

  onSubmit(): void {
    const {
      onSubmit,
      onboarding,
      onSubmitTermsAndConditionsFn,
      missingTermsValidation,
      hasTermsToAgree,
    } = this.props;

    // Early return - missing accept terms OR has already accepted terms
    if (missingTermsValidation) {
      this.setState({ ctaClicked: true, errorOnSubmit: null });
      return;
    }

    this.setState({ isLoading: true, errorOnSubmit: null });

    (hasTermsToAgree ? onSubmitTermsAndConditionsFn() : Promise.resolve())
      .then(() => onboarding.submitApplication())
      .then(() => {
        this.setState({ isLoading: false });
        return onSubmit();
      })
      .catch(error => {
        this.setState({ isLoading: false, errorOnSubmit: error });

        // Scroll to the top
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
      });
  }

  render(): React.JSX.Element {
    const {
      bpcPayloadWithTerms,
      onSummaryPageBack,
      t,
      error,
      onboarding,
      TermsAndConditionsComponent,
    } = this.props;
    const { errorOnSubmit, isLoading, ctaClicked } = this.state;
    const { has_agreed_terms_and_conditions: hasAcceptedTerms } = bpcPayloadWithTerms || {};
    return (
      <ContainerTemplate title={t('group.summary.title')} error={error || errorOnSubmit}>
        {onboarding.onboardingGroups
          .filter(g => g.isApplicable())
          .map(onboardingGroup => (
            <React.Fragment key={onboardingGroup.groupId}>
              {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
              {/* @ts-ignore mage-components withResponsive is not typed so it breaks the props. */}
              <SummarySection
                title={t(onboardingGroup.labelTitle, {}, onboardingGroup.fallbackTitle)}
                subtitle={t(onboardingGroup.labelSubtitle, {}, onboardingGroup.fallbackSubtitle)}
              >
                <Row>
                  {onboardingGroup.fields.map(fields =>
                    fields.map(field => (
                      <OnboardingSummaryFieldComponent
                        field={field}
                        key={field.absolutePath}
                        t={t}
                        value={onboardingGroup.getValueForField(field.absolutePath)}
                      />
                    )),
                  )}
                </Row>
              </SummarySection>
              <SpacerVertical spaceToken="space/300" />
            </React.Fragment>
          ))}
        <SpacerVertical spaceToken="space/300" />
        <TermsAndConditionsComponent isLoading={isLoading} ctaClicked={ctaClicked} />
        <HorizontalWrapper display="inline-flex" alignItems="center" justifyContent="flex-end">
          <SecondaryButton onClick={onSummaryPageBack}>
            {t(`${tPrefixButtons}.back`)}
          </SecondaryButton>
          <SpacerHorizontal spaceToken="space/300" />
          <CallToActionButton
            onClick={this.onSubmit}
            loading={isLoading}
            disabled={hasAcceptedTerms}
          >
            {t(`${tPrefixButtons}.submit_application`)}
          </CallToActionButton>
        </HorizontalWrapper>
      </ContainerTemplate>
    );
  }
}

export const OnboardingSummaryComponent = WithTermsAndConditions<OnboardingSummaryComponentProps>(
  _OnboardingSummaryComponent,
);
